import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RT, ResponsiveContainer, Cell } from "recharts";
import { useAppearance } from "../../contexts/AppearanceContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Download, TrendingUp, Target, Clock, AlertCircle } from "lucide-react";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtK(v) { if (!v) return "R$ 0";
  if (v >= 1000000) return `R$ ${(v/1000000).toFixed(1)}M`;
  if (v >= 1000) return `R$ ${(v/1000).toFixed(0)}k`;
  return `R$ ${Math.round(v)}`;
}
function fmt(v) { return new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL",maximumFractionDigits:0}).format(v||0); }

export default function ForecastReport({filterHook }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState("");
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3b82f6";

  useEffect(() => {
    // Sync with FilterBar pipeline selection
    if (filterHook.filters.pipelineIds.length > 0) {
      setSelectedPipeline(filterHook.filters.pipelineIds[0]);
    } else if (filterHook.pipelines.length && !selectedPipeline) {
      setSelectedPipeline(filterHook.pipelines[0]?.id || "");
    }
  }, [filterHook.filters.pipelineIds, filterHook.pipelines]); // eslint-disable-line

  const fetchData = useCallback(async () => {
    if (!selectedPipeline) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterHook.filters.responsavelIds.length === 1) {
        params.set("responsavel_id", filterHook.filters.responsavelIds[0]);
      }
      const res = await axios.get(`${API}/reports/forecast/${selectedPipeline}?${params}`, { withCredentials: true });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedPipeline, filterHook.filters.responsavelIds]);

  useEffect(() => { if (selectedPipeline) fetchData(); }, [fetchData, selectedPipeline]);

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data.by_stage.map(s => ({
      "Etapa": s.stage_name, "Deals em Aberto": s.deals,
      "MRR em Aberto": s.mrr_open, "Probabilidade %": s.probability,
      "MRR Forecast": s.mrr_forecast, "TCV Forecast": s.tcv_forecast,
    })));
    XLSX.utils.book_append_sheet(wb, ws, "Forecast");
    XLSX.writeFile(wb, `forecast_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Select value={selectedPipeline} onValueChange={setSelectedPipeline}>
            <SelectTrigger className="w-48 h-9 text-sm"><SelectValue placeholder="Selecionar pipeline..." /></SelectTrigger>
            <SelectContent>{filterHook.pipelines.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-slate-400">{t("ui.taxa_historica_desc")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportExcel} className="gap-2 text-slate-600">
          <Download className="w-4 h-4" />{t("relatorios.exportExcel")}</Button>
      </div>

      {loading && <div className="flex items-center justify-center h-32 text-slate-400 text-sm">{t("ui.calculando_forecast")}</div>}

      {!loading && data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "MRR em Aberto", value: fmtK(data.total_open_mrr), sub: `${data.total_open_deals} deals ativos`, icon: TrendingUp },
              { title: "MRR Forecast", value: fmtK(data.forecast_mrr), sub: "ponderado pela probabilidade", icon: Target },
              { title: "TCV Forecast", value: fmtK(data.forecast_tcv), sub: "MRR×12 + Setup esperado", icon: TrendingUp },
              { title: "Taxa Histórica", value: `${data.overall_win_rate}%`, sub: `${data.total_won}/${data.total_closed} fechamentos`, icon: Clock },
            ].map(k => (
              <div key={k.title} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight">{k.title}</p>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${tc500}18` }}>
                    <k.icon className="w-4 h-4" style={{ color: tc500 }} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900 mb-1">{k.value}</p>
                <p className="text-xs text-slate-400">{k.sub}</p>
              </div>
            ))}
          </div>

          {/* Forecast por etapa */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700">{t("relatorios.forecast")}</h3>
              <p className="text-xs text-slate-400 mt-0.5">Probabilidade calculada com base na taxa histórica de conversão de cada posição no funil</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 bg-slate-50">
                  {["Etapa","Deals","MRR em Aberto","Probabilidade","MRR Forecast","TCV Forecast"].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(data.by_stage || []).map(s => (
                    <tr key={s.stage_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{backgroundColor:s.color}} />
                          <span className="font-medium text-slate-800">{s.stage_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{s.deals}</td>
                      <td className="px-4 py-3 font-medium text-slate-700">{fmtK(s.mrr_open)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-slate-100 rounded-full h-2">
                            <div className="h-2 rounded-full" style={{width:`${s.probability}%`,backgroundColor:tc500}} />
                          </div>
                          <span className="text-xs font-semibold text-slate-700 whitespace-nowrap">{s.probability}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold" style={{color:tc500}}>{fmtK(s.mrr_forecast)}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtK(s.tcv_forecast)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50">
                    <td className="px-4 py-3 font-bold text-slate-800" colSpan={2}>Total</td>
                    <td className="px-4 py-3 font-bold text-slate-800">{fmtK(data.total_open_mrr)}</td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3 font-bold" style={{color:tc500}}>{fmtK(data.forecast_mrr)}</td>
                    <td className="px-4 py-3 font-bold text-slate-700">{fmtK(data.forecast_tcv)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Bar chart */}
          {data.by_stage?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">{t("ui.mrr_aberto_vs_forecast")}</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.by_stage} margin={{top:0,right:10,left:10,bottom:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="stage_name" tick={{fontSize:10}} />
                  <YAxis tick={{fontSize:10}} tickFormatter={v => fmtK(v)} />
                  <RT formatter={v => [fmt(v)]} contentStyle={{fontSize:12}} />
                  <Bar dataKey="mrr_open" name="MRR Aberto" fill="#e2e8f0" radius={[4,4,0,0]} />
                  <Bar dataKey="mrr_forecast" name="Forecast" fill={tc500} radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}
